/**
 * Goudstaven API Service
 * Custom Influence.io Launcher API wrapper for Clean Nutrition loyalty program.
 * Replaces default widget with custom UI components.
 */
window.GoudstavenAPI = (function () {
  'use strict';

  const BASE_URL = 'https://launcher.api.influence.io/launcher';
  let _config = null;
  let _customerCache = null;
  let _shopCache = {};
  let _loadPromise = null;

  // ========================================
  // INIT
  // ========================================

  function init() {
    const el = document.querySelector('.influenceio-widgets-init');
    if (!el) return null;

    _config = {
      shop: el.dataset.shop,
      customerId: el.dataset.customerId || null,
      customerEmail: el.dataset.customerEmail || null,
      digest: el.dataset.customerDigest || null,
    };

    return _config;
  }

  function getConfig() {
    if (!_config) init();
    return _config;
  }

  function isLoggedIn() {
    const cfg = getConfig();
    return cfg && cfg.customerId && cfg.digest;
  }

  // ========================================
  // HTTP
  // ========================================

  function buildParams(params) {
    const filtered = {};
    Object.keys(params).forEach(function (k) {
      if (params[k] != null && params[k] !== '') filtered[k] = params[k];
    });
    return new URLSearchParams(filtered).toString();
  }

  function get(path, params) {
    var qs = params ? buildParams(params) : '';
    var url = BASE_URL + path + (qs ? '?' + qs : '');

    return fetch(url, {
      headers: { 'content-type': 'application/json' },
    }).then(function (r) {
      if (!r.ok) throw new Error('GoudstavenAPI: ' + r.status + ' on ' + path);
      return r.json();
    });
  }

  function post(path, body) {
    return fetch(BASE_URL + path, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    }).then(function (r) {
      if (!r.ok) throw new Error('GoudstavenAPI: ' + r.status + ' on ' + path);
      return r.json();
    });
  }

  function put(path, body) {
    return fetch(BASE_URL + path, {
      method: 'PUT',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    }).then(function (r) {
      if (!r.ok) throw new Error('GoudstavenAPI: ' + r.status + ' on ' + path);
      return r.json();
    });
  }

  // ========================================
  // AUTH PARAMS (reusable for customer endpoints)
  // ========================================

  function authParams(extra) {
    var cfg = getConfig();
    var base = {
      customer_id: cfg.customerId,
      customer_email: cfg.customerEmail,
      shop: cfg.shop,
      digest: cfg.digest,
    };
    if (extra) {
      Object.keys(extra).forEach(function (k) {
        base[k] = extra[k];
      });
    }
    return base;
  }

  // ========================================
  // CUSTOMER ENDPOINTS
  // ========================================

  /** Get full customer profile including pointBalance, tier, referralLink */
  function getCustomer(forceRefresh) {
    if (_customerCache && !forceRefresh) return Promise.resolve(_customerCache);
    if (!isLoggedIn()) return Promise.resolve(null);

    return get('/v1/customer', authParams()).then(function (data) {
      _customerCache = data;
      return data;
    });
  }

  /** Get points activity history */
  function getPointsHistory(limit, nextToken) {
    if (!isLoggedIn()) return Promise.resolve({ items: [] });

    var params = authParams({ limit: limit || 20 });
    if (nextToken) params.nextToken = nextToken;
    return get('/v1/customer/points', params);
  }

  /** Get all active (redeemable) rewards */
  function getActiveRewards(limit, type) {
    if (!isLoggedIn()) return Promise.resolve({ items: [] });

    var params = authParams({ limit: limit || 20 });
    if (type) params.type = type;
    return get('/v1/customer/all-rewards', params);
  }

  /** Get count of active rewards */
  function getActiveRewardsCount() {
    if (!isLoggedIn()) return Promise.resolve(0);
    return get('/v1/customer/all-rewards-count', authParams());
  }

  /** Redeem a reward - returns { success, couponCode } */
  function redeemReward(ruleId, points) {
    if (!isLoggedIn()) return Promise.reject(new Error('Not logged in'));

    var cfg = getConfig();
    var body = {
      customer: { id: cfg.customerId, email: cfg.customerEmail },
      shop: cfg.shop,
      digest: cfg.digest,
      ruleId: ruleId,
      redemptionSource: 'goudstaven-custom-widget',
    };
    if (points) body.points = points;

    return post('/v1/customer/redeem', body).then(function (data) {
      // Invalidate cache after redeem
      _customerCache = null;
      return data;
    });
  }

  /** Get referral activity */
  function getReferrals(limit) {
    if (!isLoggedIn()) return Promise.resolve({ items: [] });
    return get('/v1/customer/referrals', authParams({ limit: limit || 20 }));
  }

  /** Trigger social action (e.g. Instagram follow) */
  function actionSocial(actionType) {
    if (!isLoggedIn()) return Promise.reject(new Error('Not logged in'));

    var cfg = getConfig();
    return post('/v1/customer/social', {
      customer: { id: cfg.customerId, email: cfg.customerEmail },
      shop: cfg.shop,
      digest: cfg.digest,
      action: actionType,
    });
  }

  /** Update birthday */
  function updateBirthday(day, month) {
    if (!isLoggedIn()) return Promise.reject(new Error('Not logged in'));

    var cfg = getConfig();
    return put('/v1/customer/birthday', {
      customer: { id: cfg.customerId, email: cfg.customerEmail },
      shop: cfg.shop,
      digest: cfg.digest,
      birthdayDay: day,
      birthdayMonth: month,
    });
  }

  // ========================================
  // SHOP ENDPOINTS (no auth needed)
  // ========================================

  function shopParams() {
    return { shop: getConfig().shop };
  }

  /** Get all loyalty tiers with thresholds and perks */
  function getTiers() {
    if (_shopCache.tiers) return Promise.resolve(_shopCache.tiers);

    return get('/v1/shop/tiers', shopParams()).then(function (data) {
      _shopCache.tiers = data;
      return data;
    });
  }

  /** Get single tier by ID */
  function getTier(tierId) {
    return get('/v1/shop/tiers/' + tierId, shopParams());
  }

  /** Get all earn rules */
  function getEarnRules() {
    if (_shopCache.earnRules) return Promise.resolve(_shopCache.earnRules);

    return get('/v1/shop/rules/earn', shopParams()).then(function (data) {
      _shopCache.earnRules = data;
      return data;
    });
  }

  /** Get all redeem rules */
  function getRedeemRules() {
    if (_shopCache.redeemRules) return Promise.resolve(_shopCache.redeemRules);

    return get('/v1/shop/rules/redeem', shopParams()).then(function (data) {
      _shopCache.redeemRules = data;
      return data;
    });
  }

  /** Get referral rules */
  function getReferralRules() {
    return get('/v1/shop/rules/referral', shopParams());
  }

  /** Get shop info */
  function getShop() {
    return get('/v1/shop', shopParams());
  }

  // ========================================
  // HELPERS
  // ========================================

  /** Format number with dots (NL style): 1500 -> "1.500" */
  function formatPoints(num) {
    if (num == null) return '0';
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  }

  /**
   * Determine tier progress as percentage (0-100).
   * Requires customer data and tiers data.
   */
  function getTierProgress(customer, tiers) {
    if (!customer || !tiers || !tiers.rules) return null;

    var sorted = tiers.rules
      .slice()
      .sort(function (a, b) {
        return parseInt(a.threshold) - parseInt(b.threshold);
      });

    var currentTierId = customer.currentLoyaltyTierId;
    var progress = customer.currentLoyaltyTierProgress || customer.pointBalance || 0;

    var currentIdx = -1;
    sorted.forEach(function (t, i) {
      if (t.id === currentTierId) currentIdx = i;
    });

    // If at the highest tier
    if (currentIdx >= sorted.length - 1) {
      return { percentage: 100, currentTier: sorted[currentIdx], nextTier: null, pointsToNext: 0 };
    }

    var nextTier = sorted[currentIdx + 1];
    var currentThreshold = currentIdx >= 0 ? parseInt(sorted[currentIdx].threshold) : 0;
    var nextThreshold = parseInt(nextTier.threshold);
    var range = nextThreshold - currentThreshold;
    var progressInRange = progress - currentThreshold;
    var pct = range > 0 ? Math.min(100, Math.max(0, Math.round((progressInRange / range) * 100))) : 0;

    return {
      percentage: pct,
      currentTier: currentIdx >= 0 ? sorted[currentIdx] : null,
      nextTier: nextTier,
      pointsToNext: Math.max(0, nextThreshold - progress),
    };
  }

  /**
   * Beschrijf een redeem rule of geclaimde reward voor weergave.
   * Redeem rules (shop-endpoint) hebben templateName + product;
   * geclaimde rewards (customer/all-rewards) hebben rewardType.
   * Giftcards leveren een tegoedbon op; productdiscount-rewards (zoals
   * Training in de Gouden Tempel) leveren een kortingscode op waarmee je
   * het gekoppelde product (deels) gratis bestelt.
   */
  function getRewardMeta(rule) {
    var type = (rule.templateName || rule.rewardType || '').toLowerCase();
    var product = rule.product || null;
    var meta = {
      isGiftcard: type.indexOf('giftcard') !== -1,
      isFreeProduct: false,
      badge: null,
      note: null,
      imageUrl: null,
      productUrl: null,
      inStock: true,
    };

    if (meta.isGiftcard) {
      meta.badge = 'Tegoedbon';
      meta.note = 'Je ontvangt een kortingscode ter waarde van dit bedrag.';
      return meta;
    }

    if (type.indexOf('productdiscount') !== -1) {
      if (product) {
        meta.imageUrl = product.imageUrl || null;
        meta.productUrl = product.externalUrlHandle ? '/products/' + product.externalUrlHandle : null;
        meta.inStock = product.inStock !== false;
      }

      if (!rule.discountValue || parseInt(rule.discountValue) >= 100) {
        meta.isFreeProduct = true;
        meta.badge = 'Gratis product';
        meta.note = 'Je ontvangt een kortingscode waarmee je dit product gratis bestelt.';
      } else {
        meta.badge = rule.discountValue + '% korting';
        meta.note = 'Je ontvangt een kortingscode voor ' + rule.discountValue + '% korting op dit product.';
      }
      return meta;
    }

    if (type.indexOf('freeshipping') !== -1) {
      meta.badge = 'Gratis verzending';
    } else if (type.indexOf('percentage') !== -1) {
      meta.badge = rule.discountValue ? rule.discountValue + '% korting' : 'Korting';
    } else if (type.indexOf('amountdiscount') !== -1) {
      meta.badge = rule.discountValue ? '€' + rule.discountValue + ' korting' : 'Korting';
    }

    return meta;
  }

  /**
   * Bepaalt of een redeem rule nog daadwerkelijk verzilverd kan worden.
   * Influence.io laat rewards soms staan als status "Active" terwijl het
   * gekoppelde product in Shopify is verwijderd of gearchiveerd - dan is
   * `product` afwezig of heeft het een status != "active". Zulke rewards
   * kun je niet claimen, dus filteren we ze weg (spiegelt isRuleActive in
   * de missions-sectie). Giftcards hebben geen product en blijven zichtbaar
   * zolang de rule Active is. Een product dat nog bestaat maar tijdelijk
   * uitverkocht is (inStock === false) blijft wel zichtbaar maar disabled -
   * dat regelt getRewardMeta().inStock in de render.
   */
  function isRewardRedeemable(rule) {
    if (!rule) return false;
    if (rule.status && String(rule.status).toLowerCase() !== 'active') return false;

    var type = (rule.templateName || '').toLowerCase();
    if (type.indexOf('productdiscount') !== -1) {
      var product = rule.product;
      if (!product) return false; // product verwijderd
      if (product.status && String(product.status).toLowerCase() !== 'active') return false; // gearchiveerd / concept
    }
    return true;
  }

  /**
   * Get tier-specific gradient colors for membership cards.
   * Returns { bg, bgHover, text, accent } per tier title.
   */
  function getTierColors(tierTitle) {
    var title = (tierTitle || '').toLowerCase();

    var palettes = {
      brons: {
        gradient: 'linear-gradient(135deg, #ba784d 0%, #8b4622 100%)',
        accent: '#d4915f',
        text: '#fff',
        badge: '#cd7f32',
      },
      zilver: {
        gradient: 'linear-gradient(135deg, #c0c0c0 0%, #808080 100%)',
        accent: '#d4d4d4',
        text: '#fff',
        badge: '#c0c0c0',
      },
      goud: {
        gradient: 'linear-gradient(135deg, #f2c94c 0%, #c8951a 100%)',
        accent: '#f5d76e',
        text: '#1a1a1a',
        badge: '#f2c94c',
      },
      diamant: {
        gradient: 'linear-gradient(135deg, #b9f2ff 0%, #4fc3f7 50%, #0288d1 100%)',
        accent: '#81d4fa',
        text: '#0a1628',
        badge: '#4fc3f7',
      },
    };

    // Match partial
    var match = null;
    Object.keys(palettes).forEach(function (key) {
      if (title.indexOf(key) !== -1) match = palettes[key];
    });

    return match || {
      gradient: 'linear-gradient(135deg, #333 0%, #111 100%)',
      accent: '#555',
      text: '#fff',
      badge: '#444',
    };
  }

  // ========================================
  // EVENTS
  // ========================================

  function emit(eventName, detail) {
    document.dispatchEvent(new CustomEvent('goudstaven:' + eventName, { detail: detail }));
  }

  /**
   * Initialize and load all data, then fire events.
   * Call this on DOMContentLoaded. Idempotent: meerdere secties op een
   * pagina mogen dit veilig aanroepen, er wordt maar 1x geladen.
   */
  function load() {
    if (_loadPromise) return _loadPromise;

    var cfg = init();
    if (!cfg) return Promise.resolve();

    var promises = [getTiers(), getEarnRules(), getRedeemRules(), getReferralRules()];
    if (isLoggedIn()) {
      promises.push(getCustomer());
    }

    _loadPromise = Promise.all(promises).then(function (results) {
      var payload = {
        tiers: results[0],
        earnRules: results[1],
        redeemRules: results[2],
        referralRules: results[3],
        customer: results[4] || null,
        isLoggedIn: isLoggedIn(),
      };

      if (payload.customer && payload.tiers) {
        payload.tierProgress = getTierProgress(payload.customer, payload.tiers);
      }

      emit('loaded', payload);
      return payload;
    }).catch(function (err) {
      console.error('[GoudstavenAPI]', err);
      emit('error', { error: err });
      _loadPromise = null;
    });

    return _loadPromise;
  }

  // ========================================
  // PUBLIC API
  // ========================================

  return {
    init: init,
    load: load,
    isLoggedIn: isLoggedIn,

    // Customer
    getCustomer: getCustomer,
    getPointsHistory: getPointsHistory,
    getActiveRewards: getActiveRewards,
    getActiveRewardsCount: getActiveRewardsCount,
    redeemReward: redeemReward,
    getReferrals: getReferrals,
    actionSocial: actionSocial,
    updateBirthday: updateBirthday,

    // Shop
    getTiers: getTiers,
    getTier: getTier,
    getEarnRules: getEarnRules,
    getRedeemRules: getRedeemRules,
    getReferralRules: getReferralRules,
    getShop: getShop,

    // Helpers
    formatPoints: formatPoints,
    getTierProgress: getTierProgress,
    getTierColors: getTierColors,
    getRewardMeta: getRewardMeta,
    isRewardRedeemable: isRewardRedeemable,
  };
})();
